import { useI18n } from 'vue-i18n'
import { cloneJson, readPath, writePath } from '@drama/novel-authoring'
import { removeEntry } from '@drama/novel-authoring'
import { withNovelCharacterCardStates } from '@drama/contracts/novel'

function within(path, prefix) {
  return prefix === '' || path === prefix || path.startsWith(`${prefix}.`)
}

/**
 * 分区编辑器的字段绑定。`modelValue` 是整本作品的工作副本，路径从作品根写起（如 `['characters', 0, 'name']`），
 * 所以分区能连带改写别的分区的键（新增人物同时写起始位置，删物品同时删开场状态）。
 * 单字段写入走 writePath（结构共享）；增删条目这类多处联动的改动走 mutate（克隆整本后改）。
 * `issues` 是落在本分区的校验错误（`{ key, path, note }`）：控件按路径描红，并带 `data-path` 供"去该处"聚焦。
 * @param {{ modelValue: Record<string, any>, issues: Array<{ key: string, path: string, note: string }> }} props
 * @param {(event: 'update:modelValue'|'blocked', value: any) => void} emit
 */
export function useSectionModel(props, emit) {
  const { t } = useI18n()

  function read(path) {
    return readPath(props.modelValue, path)
  }

  function write(path, value) {
    emit('update:modelValue', withNovelCharacterCardStates(writePath(props.modelValue, path, value)))
  }

  function mutate(change) {
    const next = cloneJson(props.modelValue)
    change(next)
    emit('update:modelValue', withNovelCharacterCardStates(next))
  }

  /** 删掉列表里的一项（没有别处引用的子项：演出状态、线索、示例、关系、事项等）。 */
  function removeAt(path) {
    const at = path[path.length - 1]
    write(path.slice(0, -1), (read(path.slice(0, -1)) ?? []).filter((_, index) => index !== at))
  }

  /** 删除可被引用的条目（人物、地点、物品、事实、开场段落）：还被引用就不删，把引用位置交给编辑器列出。 */
  function removeEntity(kind, index, name) {
    const result = removeEntry(props.modelValue, kind, index)
    if (result.blocking.length > 0) emit('blocked', { name, references: result.blocking })
    else if (result.pack) emit('update:modelValue', result.pack)
  }

  function invalid(path) {
    const prefix = path.join('.')
    return props.issues.some(issue => within(issue.path, prefix))
  }

  /** 落在某条目（或整个分区：传空路径）里的错误说明。 */
  function notes(path) {
    const prefix = path.join('.')
    return props.issues.filter(issue => within(issue.path, prefix)).map(issue => ({ key: issue.key, text: issue.note }))
  }

  /** 字段下方的错误文字：空着的必填项提示"请填写"，其余显示校验原因。 */
  function error(path) {
    const prefix = path.join('.')
    const found = props.issues.filter(issue => within(issue.path, prefix))
    if (!found.length) return ''
    const value = read(path)
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)) return t('novelAuthoring.requiredEmpty')
    return [...new Set(found.map(issue => issue.reason ?? issue.note))].join(' ')
  }

  /** `<Input v-bind="bind(['characters', 0, 'name'])" />`：值、写回、描红与定位路径一次绑好。 */
  function bind(path, fallback = '') {
    return {
      modelValue: read(path) ?? fallback,
      'onUpdate:modelValue': value => write(path, value),
      invalid: invalid(path),
      'data-path': path.join('.'),
    }
  }

  /** 多选勾选框：勾上或取消 `value`，结果按 `order` 排序，不在 `order` 里的旧值一并去掉。 */
  function toggle(path, value, checked, order) {
    const chosen = new Set(read(path) ?? [])
    if (checked) chosen.add(value)
    else chosen.delete(value)
    write(path, order.filter(item => chosen.has(item)))
  }

  return { read, write, mutate, removeAt, removeEntity, invalid, notes, error, bind, toggle }
}
